/**
 * AsnMobileMetricCard
 *
 * ASN Gap–only mobile result card for the Location Summary tab.
 *
 * This intentionally does NOT reuse the shared Analytics Hub
 * `OnePagerMetricCard` (title-left / metrics-right layout). That component is
 * also rendered by the unrelated Analytics Hub KPI One Pager mobile view, so
 * changing its layout there would risk regressing a feature outside this
 * task's scope. Instead this is a small, local component used exclusively by
 * `AsnLocationDetailsMobileView`, laid out as a title header followed by a
 * single row of equal-width metric tiles — sized to stay on one row and
 * readable from 320px through 430px wide screens.
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';

export interface AsnMobileMetricItem {
  readonly label: string;
  readonly value: string;
  readonly valueColor?: string;
}

export interface AsnMobileMetricCardProps {
  readonly title: string;
  readonly metrics: readonly AsnMobileMetricItem[];
}

const AsnMobileMetricCard: React.FC<AsnMobileMetricCardProps> = ({ title, metrics }) => {
  return (
    <View style={styles.card}>
      <Text style={styles.title} numberOfLines={1}>
        {title}
      </Text>
      <View style={styles.tileRow}>
        {metrics.map((metric, index) => (
          <View key={`${metric.label}-${index}`} style={styles.tile}>
            <Text style={styles.tileLabel} numberOfLines={2}>
              {metric.label}
            </Text>
            <Text
              style={[styles.tileValue, metric.valueColor ? { color: metric.valueColor } : null]}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.7}
            >
              {metric.value}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 10,
    paddingHorizontal: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    fontFamily: 'Inter-SemiBold',
    color: '#32363F',
    marginBottom: 8,
  },
  tileRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 8,
  },
  tile: {
    flex: 1,
    minWidth: 0,
    backgroundColor: '#F5F7FA',
    borderRadius: 10,
    paddingVertical: 8,
    paddingHorizontal: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileLabel: {
    fontSize: 9,
    fontFamily: 'Inter-SemiBold',
    fontWeight: '700',
    color: '#7A7F87',
    textAlign: 'center',
    textTransform: 'uppercase',
    lineHeight: 11,
  },
  tileValue: {
    marginTop: 4,
    fontSize: 13,
    fontFamily: 'Inter-Bold',
    fontWeight: '800',
    color: '#2F2F2F',
    textAlign: 'center',
  },
});

AsnMobileMetricCard.displayName = 'AsnMobileMetricCard';

export default React.memo(AsnMobileMetricCard);
